import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, model, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

import { LinkTypeEnum, NoticeModal as NoticeModalData } from 'app/graphql/generated/types-user';
import { LinkPreview, looksLikeExternalDomain, prefixUrlWithHttp } from 'app/helpers';
import { GAEventEnum } from 'app/interfaces';
import { GoogleAnalyticsService as AnalyticsEventService } from 'app/services';
import { Button } from 'app/shared/buttons';
import { ModalBase } from 'app/shared/modal/modal/modal-base';

export type NoticeModalVariant = 'image-only' | 'text-only' | 'image-and-text';

const STEBBY_LOGO_URL = '/assets/images/logos/stebby_logo_pink_correct.svg';

export function getNoticeModalVariant(notice: NoticeModalData | null | undefined): NoticeModalVariant | null {
    if (!notice) return null;

    const hasImage = !!notice.imageUrl;
    const hasText = !!(notice.title || notice.description);

    if (hasImage && hasText) return 'image-and-text';
    if (hasImage) return 'image-only';
    if (hasText) return 'text-only';

    return null;
}

function makeNoticeModalLinkPreview(notice: NoticeModalData): LinkPreview {
    if (!notice.linkContent || notice.linkContent === '') return null;
    switch (notice.linkType) {
        case LinkTypeEnum.FILTER:
            return {
                route: ['search'],
                queryParams: {
                    other: notice.linkContent,
                },
            };
        case LinkTypeEnum.EVENT:
            return {
                route: ['event', notice.linkContent],
            };

        case LinkTypeEnum.PROVIDER:
            if (!notice.pointOfSaleHash) {
                return null;
            }

            return { route: ['pos', notice.pointOfSaleHash] };

        case LinkTypeEnum.SERVICE:
            if (!notice.posTicketSettings) {
                return null;
            }
            return {
                route: ['pos', notice.posTicketSettings.point_of_sale.hash, 'services'],
                queryParams: {
                    serviceId: notice.posTicketSettings.service.id.toString(),
                },
            };

        default:
            return null;
    }
}

@Component({
    selector: 'stby-notice-modal',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './notice-modal.html',
    styleUrl: './notice-modal.scss',
    imports: [ModalBase, Button, NgTemplateOutlet, RouterLink, TranslatePipe],
})
export class NoticeModal {
    private readonly analyticsEventService = inject(AnalyticsEventService);

    readonly visible = model<boolean>(true);
    readonly notice = input<NoticeModalData | null>(null);

    private readonly imageErrored = signal(false);

    private readonly linkPreview = computed(() => {
        const notice = this.notice();
        return notice ? makeNoticeModalLinkPreview(notice) : null;
    });

    readonly variant = computed(() => getNoticeModalVariant(this.notice()));
    readonly ctaHref = computed(() => {
        const notice = this.notice();
        if (notice?.linkType !== LinkTypeEnum.LINK || !notice.linkContent) return undefined;
        return looksLikeExternalDomain(notice.linkContent) ? prefixUrlWithHttp(notice.linkContent) : notice.linkContent;
    });
    readonly ctaRouteTo = computed(() => this.linkPreview()?.route);
    readonly ctaQueryParams = computed(() => this.linkPreview()?.queryParams ?? {});
    readonly hasResolvedCta = computed(() => !!this.ctaHref() || !!this.ctaRouteTo());

    readonly isImageOnly = computed(() => this.variant() === 'image-only');
    readonly imageIsClickable = computed(() => this.hasResolvedCta() && this.variant() === 'image-only');
    readonly hasText = computed(() => this.variant() !== null && this.variant() !== 'image-only');
    readonly imageSrc = computed(() => {
        const imageUrl = this.notice()?.imageUrl;
        return this.imageErrored() || this.variant() === 'text-only' || !imageUrl ? STEBBY_LOGO_URL : imageUrl;
    });

    onImageError(): void {
        this.imageErrored.set(true);
    }

    onCtaClick(): void {
        this.trackNoticeClick();
        this.visible.set(false);
    }

    private trackNoticeClick(): void {
        const notice = this.notice();
        if (!notice.analyticsHash) return;

        this.analyticsEventService.event(GAEventEnum.NOTICE_MODAL_CLICK, {
            NoticeID: notice.analyticsHash,
            platform: 'web',
        });
    }
}
